import dateFormat from 'dateformat';

import { Button } from '@travelnest/merlin.button';
import React from 'react';
import { ICustomer } from '../../../../data/entities/customer';
import { IPropertySummary } from '../../../../data/entities/property-summary/index';
import { channels } from '../../../../entities/contracts/constants';
import { IContracts } from '../../../../entities/contracts/types';
import { IAction } from '../../../components/action/types';
import { PropertyShellModalLauncher } from '../../../components/property-shell-modal/PropertyShellModalLauncher';
import { actionDetailsTypes } from './types';

const incomplete = (s: IPropertySummary) => !s.is_complete;
const unpublished = (s: IPropertySummary) =>
	!!(s.is_complete && !s.has_ever_published);
const stale = (s: IPropertySummary) =>
	!!(s.is_complete && s.has_ever_published && s.has_unpublished_changes);
const addAction = (action: IAction | null | undefined, actions: IAction[]) =>
	action && actions.push(action);

export const accountIsBeingVerified = (
	customer: ICustomer,
	summaries: IPropertySummary[]
) => {
	return !!customer && !customer.validated && summaries.some(unpublished);
};

export const addPropertyActions = (
	summaries: IPropertySummary[],
	actions: IAction[],
	completenessFactsForProperties: object
) => {
	if (!summaries.length) {
		addAction(buildAction(actionDetailsTypes.ADD_PROPERTY, ''), actions);
	}

	summaries
		.filter(incomplete)
		.map((s) =>
			addAction(
				buildAction(actionDetailsTypes.COMPLETE_PROPERTY, s.owner_id, [
					s,
					completenessFactsForProperties[s.id],
				]),
				actions
			)
		);
	summaries
		.filter(unpublished)
		.map((s) =>
			addAction(
				buildAction(actionDetailsTypes.PUBLISH_PROPERTY, s.owner_id, [
					s,
				]),
				actions
			)
		);
	summaries
		.filter(stale)
		.map((s) =>
			addAction(
				buildAction(actionDetailsTypes.REPUBLISH_PROPERTY, s.owner_id, [
					s,
				]),
				actions
			)
		);
};

export const buildPropertyActions = (
	summary: IPropertySummary,
	completenessFactsForProperties: object
) => {
	const actions: IAction[] = [];
	addPropertyActions([summary], actions, completenessFactsForProperties);
	return actions;
};

const dateRangeAction = (
	start: Date | null,
	end: Date | null,
	type: actionDetailsTypes,
	customerId: string,
	actions: IAction[]
) => {
	const now = new Date();
	if (customerId && (!start || start <= now) && (!end || end > now)) {
		const action = buildAction(type, customerId, [start, end]);
		addAction(action, actions);
	}
};

export const buildActions = (
	customer: ICustomer,
	summaries: IPropertySummary[] = [],
	contracts: IContracts = {} as IContracts,
	completenessFactsForProperties: object
): IAction[] => {
	const actions = [] as IAction[];

	// if (customer && !customer.validated && hasCompletedOneProperty(summaries)) {
	// temporarily removing the call to hasCompletedOneProperty as it is out of sync with TBP validation
	// and is triggering a bug where this action is never generated
	if (accountIsBeingVerified(customer, summaries)) {
		addAction(
			buildAction(actionDetailsTypes.ACCOUNT_VERIFICATION, customer.id),
			actions
		);
	}

	if (getChannelCount(contracts) < 1) {
		addAction(
			buildAction(actionDetailsTypes.NO_CHANNELS, customer.id),
			actions
		);
	}

	addPropertyActions(summaries, actions, completenessFactsForProperties);

	/* below code is commented as these actions have been moved into promo cards.
	This may not be permament, so will leave the code here in case we revert. */

	// const newestProperty = summaries.sort((a, b) => a.id > b.id ? -1 : 1)[0];
	// addAction(buildAction(actionDetailsTypes.SMART_PRICING_AVAILABLE, customer.id, [newestProperty]), actions);

	// addAction(buildAction(actionDetailsTypes.DIRECT_HUB_AVAILABLE, customer.id), actions);
	// 27/03/19 - Commenting out this code; COVID-19 cost savings.
	if (customer) {
		// dateRangeAction(new Date('2019-01-01'), null, actionDetailsTypes.PHOTOSHOOT_OFFER, customer.id, actions);
		dateRangeAction(
			new Date('2019-07-01'),
			null,
			actionDetailsTypes.REFERRAL_PROGRAMME,
			customer.id,
			actions
		);
	}

	return actions;
};

// TODO: Really need to do some investigation into this one for button styling
const getStartedCta = (
	<PropertyShellModalLauncher>
		<Button onClick={(e) => e.preventDefault()}>Get started!</Button>
	</PropertyShellModalLauncher>
);

export const buildAction = (
	actionType: string,
	customerId: string,
	additionalInfo: any[] = []
): IAction | null => {
	let summary: IPropertySummary;

	switch (actionType) {
		case actionDetailsTypes.ACCOUNT_VERIFICATION:
			return {
				title: 'You are being verified',
				/* text is instead defined in the <Home /> component as it requires formatting.
				empty string passed to satisfy type without changing it and causing type errors elsewhere. */
				text: '',
				classification: 'mandatory',
				type: actionType,
				priority: 1,
				consumer: {
					id: customerId,
					type: 'customer',
				},
				// details were no longer relevant but removing the object caused a lot of tests to fail
				details: {
					info: [],
					help: [],
					helpLinks: [],
				},
			};
		case actionDetailsTypes.NO_CHANNELS:
			return {
				priority: 3,
				type: actionType,
				title: 'Select marketing channels',
				text: "Tell us which channels you'd like to list your property on.",
				classification: 'mandatory',
				link: '/redirect/channels',
				linkLabel: 'Select channels',
				consumer: {
					id: customerId,
					type: 'customer',
				},
			};
		case actionDetailsTypes.NO_CANCELLATION_PREFERENCE_SELECTED:
			return {
				type: actionType,
				title: `Review cancellation preference`,
				text: 'We recommend choosing the Smart cancellation preference to maximise bookings.',
				classification: 'mandatory',
				priority: 10,
				link: '/redirect/channels',
				linkLabel: 'Review cancellation preference',
				consumer: {
					id: customerId,
					type: 'customer',
				},
			};
		case actionDetailsTypes.ADD_PROPERTY:
			return {
				priority: 1,
				type: actionType,
				title: 'Add your first property',
				text: [
					'To get started with Travelnest, we need to ask you some questions about your property.',
					"We've made this process as simple as possible, and you can reach out to us for help if you need it.",
				],
				classification: 'special',
				cta: getStartedCta,
				icon: 'mr-nest',
				link: '/',
				linkLabel: 'add property',
				consumer: {
					id: customerId,
					type: 'customer',
				},
			};
		case actionDetailsTypes.COMPLETE_PROPERTY:
			summary = additionalInfo[0] as IPropertySummary;
			const completenessFactForProperty = additionalInfo[1];
			const link =
				completenessFactForProperty === 'complete'
					? `/property/${summary.id}/edit`
					: `/property/${summary.id}/add/tasks/${completenessFactForProperty}`;
			return {
				type: actionType,
				title: 'More information needed',
				text: `We need some more details about ${summary.internal_name} before we can create your listings.`,
				classification: 'special',
				priority: 3,
				link,
				linkLabel: 'Complete property profile',
				icon: summary,
				consumer: {
					id: summary.id,
					type: 'property',
				},
			};
		case actionDetailsTypes.PUBLISH_PROPERTY:
			summary = additionalInfo[0] as IPropertySummary;
			return {
				type: actionType,
				title: 'Publish property now',
				// tslint:disable-next-line:max-line-length
				text: `${summary.name} is complete and ready to go live on your selected channels. Publish now and get ready to be seen by millions of guests!`,
				classification: 'special',
				priority: 2,
				link: `/property/${summary.id}/edit`,
				linkLabel: 'Publish property',
				icon: summary,
				consumer: {
					id: summary.id,
					type: 'property',
				},
			};
		case actionDetailsTypes.REPUBLISH_PROPERTY:
			summary = additionalInfo[0] as IPropertySummary;
			return {
				type: actionType,
				title: 'Share your changes with guests!',
				text: [
					"You've made changes to your property information since it was last published.",
					'Publish this property now to send the changes to your selected marketing channels.',
				],
				classification: 'special',
				priority: 5,
				link: `/property/${summary.id}/edit`,
				linkLabel: 'Publish changes now',
				icon: summary,
				consumer: {
					id: summary.id,
					type: 'property',
				},
			};
		case actionDetailsTypes.DIRECT_HUB_AVAILABLE:
			return {
				type: actionType,
				title: 'Direct',
				text: [
					'Promote and receive guest bookings through your unique web page with a flat 5% Travelnest fee.',
				],
				classification: 'mandatory',
				priority: 1,
				link: '/direct',
				linkLabel: 'Manage your listings',
				consumer: {
					id: customerId,
					type: 'customer',
				},
			};

		case actionDetailsTypes.SMART_PRICING_AVAILABLE:
			const property = additionalInfo[0] as IPropertySummary;
			const tbpMode = property.has_ever_published ? 'edit' : 'add';
			return {
				type: actionType,
				title: 'Smart Pricing',
				text: [
					'Let us regularly update your prices to set competitive, smart rates.',
				],
				classification: 'promo',
				priority: 1,
				link: `/property/${property.id}/${tbpMode}/tasks/pricing`,
				linkLabel: 'Go to pricing',
				consumer: {
					id: customerId,
					type: 'customer',
				},
			};

		default:
			return null;
	}
};

export const getChannelCount = (contracts: IContracts) => {
	const allowed = channels.map((c) => c.id);
	const filtered = Object.keys(contracts).filter(
		(key) => allowed.includes(key) && contracts[key]
	);
	return filtered.length;
};

export const hasCompletedOneProperty = (summaries: IPropertySummary[]) =>
	!!summaries.find((summary) => summary.is_complete);

export const formatDate = (date: Date) => {
	return dateFormat(date, 'dddd, dS mmmm yyyy');
};
